/**
 * Fetch with a hard timeout (AbortController) and bounded, error-type-aware
 * retries. Used for the manifest bootstrap so a slow/stuck gateway surfaces as
 * an error (which renders the existing Retry UI) instead of leaving the user on
 * an infinite "Loading Tyndo…" splash.
 *
 * Retry policy is differentiated by failure class — a generic "retry anything a
 * few times" loop wastes time hammering a permanently-broken gateway and
 * re-issues failures that will never recover:
 *   - 2xx/3xx            → return immediately.
 *   - 4xx                → permanent (bad request / unauthorized / not found);
 *                          return straight through, NEVER retry — the response
 *                          won't change on a re-issue and the caller surfaces it.
 *   - 5xx                → transient server error; retry up to `retries`.
 *   - timeout (our abort) → slow gateway; retry ONCE regardless of `retries`,
 *                          since a second timeout almost always means the gateway
 *                          is genuinely stuck rather than momentarily slow.
 *   - external abort      → caller cancelled (tenant switch / unmount); do NOT
 *                          retry and propagate the AbortError.
 *   - network error       → connection refused / DNS / offline; retry up to
 *                          `retries`.
 *
 * Backoff is exponential with full jitter (min(500 * 2^attempt, cap) +
 * random[0,500)) and capped at 10s so the worst-case wait between attempts
 * stays bounded.
 */
export const BACKOFF_CAP_MS = 10_000;
// A timeout is retried at most this many times even if `retries` is higher.
export const MAX_TIMEOUT_RETRIES = 1;

export async function fetchWithTimeoutRetry(
  url: string,
  init: RequestInit,
  opts: { timeoutMs?: number; retries?: number } = {},
): Promise<Response> {
  const timeoutMs = opts.timeoutMs ?? 8000;
  const retries = opts.retries ?? 2;
  let timeoutAttempts = 0;
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    // Honor a caller-supplied signal (e.g. tenant switch / unmount) by chaining
    // it into our controller, so an external cancel aborts the in-flight fetch
    // and is distinguishable from our own timeout below.
    const externalSignal = init.signal;
    const onExternalAbort = () => controller.abort();
    if (externalSignal) {
      if (externalSignal.aborted) controller.abort();
      else externalSignal.addEventListener('abort', onExternalAbort, { once: true });
    }

    let shouldRetry = false;
    try {
      // audit-allow: direct-fetch -- transport layer: this util IS the shared
      // shell transport that implements timeout + bounded retry + abort
      // chaining; everything else routes through it rather than calling fetch.
      const response = await fetch(url, { ...init, signal: controller.signal });
      // 4xx is permanent — return it so the caller can surface the real status.
      // 5xx is transient — retry while attempts remain; otherwise return it.
      if (response.status >= 500 && attempt < retries) {
        lastError = new Error(`Gateway returned ${response.status}`);
        shouldRetry = true;
      } else {
        return response;
      }
    } catch (err) {
      lastError = err;
      const isAbort =
        (typeof DOMException !== 'undefined' &&
          err instanceof DOMException &&
          err.name === 'AbortError') ||
        (err instanceof Error && err.name === 'AbortError');
      if (isAbort && !timedOut) {
        // External cancellation — do not retry; let the caller observe it.
        throw err;
      }
      if (timedOut) {
        // Slow/stuck gateway: retry at most MAX_TIMEOUT_RETRIES times.
        timeoutAttempts += 1;
        shouldRetry = timeoutAttempts <= MAX_TIMEOUT_RETRIES && attempt < retries;
        lastError = new Error(`Request to ${url} timed out after ${timeoutMs}ms`);
      } else {
        // Network-level failure (connection refused, DNS, offline): retry.
        shouldRetry = attempt < retries;
      }
    } finally {
      clearTimeout(timer);
      if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);
    }

    if (!shouldRetry) break;

    // Full-jitter exponential backoff, capped at BACKOFF_CAP_MS.
    const base = Math.min(500 * Math.pow(2, attempt), BACKOFF_CAP_MS);
    const delay = Math.min(base + Math.random() * 500, BACKOFF_CAP_MS);
    await new Promise((r) => setTimeout(r, delay));
  }

  const reason = lastError instanceof Error ? lastError.message : 'request failed';
  throw new Error(
    `Manifest request timed out or failed after ${retries + 1} attempts (${reason})`,
  );
}
